
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useBilling } from '@/hooks/billing/useBilling';
import { useAdminStatus } from '@/hooks/admin/useAdminStatus';
import BillingNavbar from '@/components/billing/BillingNavbar';
import WaveSyncStatus from '@/components/billing/WaveSyncStatus';
import { Invoice } from '@/services/billing/billingService';
import { format } from 'date-fns';
import { Calendar, FileText, ExternalLink } from 'lucide-react';
import { generateInvoicePDF } from '@/utils/pdfGenerator';

const InvoicesPage: React.FC = () => {
  const { invoices, isLoading, scheduleInvoice, refreshData } = useBilling();
  const { isAdmin } = useAdminStatus();
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [scheduledDate, setScheduledDate] = useState('');

  const handleScheduleInvoice = async () => {
    if (selectedInvoice && scheduledDate) {
      await scheduleInvoice(selectedInvoice.id, scheduledDate);
      setSelectedInvoice(null);
      setScheduledDate('');
    }
  };

  const handleGeneratePDF = (invoice: Invoice) => {
    generateInvoicePDF(invoice);
  };

  const getStatusColor = (status: Invoice['status']) => {
    switch (status) {
      case 'paid': return 'bg-green-500';
      case 'overdue': return 'bg-red-500';
      case 'unpaid': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  const getWaveInvoiceUrl = (waveInvoiceId: string) => {
    // This would be the actual Wave invoice URL - you'd need to get this from Wave's API
    return `https://wave.waveapps.com/invoice/${waveInvoiceId}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading invoices...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <BillingNavbar />
      
      <div className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Invoices</h1>
          <p className="text-gray-600">
            {isAdmin 
              ? 'Manage client invoices and payment tracking'
              : 'View your invoices and payment history'
            }
          </p>
        </div>

        {invoices.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-gray-500">No invoices found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {invoices.map((invoice) => (
              <Card key={invoice.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">
                      Invoice #{invoice.id.slice(-8)}
                    </CardTitle>
                    <Badge className={getStatusColor(invoice.status)}>
                      {invoice.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600">
                    Created: {format(new Date(invoice.created_at), 'MMM d, yyyy')}
                  </p>
                </CardHeader>

                <CardContent>
                  <div className="space-y-4">
                    <div className="text-2xl font-bold">${invoice.total.toFixed(2)}</div>
                    
                    {invoice.scheduled_date && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar className="h-4 w-4 mr-2" />
                        Scheduled: {format(new Date(invoice.scheduled_date), 'MMM d, yyyy')}
                      </div>
                    )}

                    {invoice.due_date && (
                      <div className="text-sm text-gray-600">
                        Due: {format(new Date(invoice.due_date), 'MMM d, yyyy')}
                      </div>
                    )}

                    {invoice.paid_at && (
                      <div className="text-sm text-green-600">
                        Paid: {format(new Date(invoice.paid_at), 'MMM d, yyyy')}
                      </div>
                    )}

                    <WaveSyncStatus
                      syncStatus={invoice.wave_sync_status}
                      syncError={invoice.wave_sync_error}
                      waveId={invoice.wave_invoice_id}
                      itemType="invoice"
                      itemId={invoice.id}
                      onSyncComplete={refreshData}
                    />

                    <div className="flex flex-col space-y-2">
                      <Button
                        variant="outline"
                        onClick={() => handleGeneratePDF(invoice)}
                        className="flex items-center"
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Download PDF
                      </Button>

                      {invoice.wave_invoice_id && (
                        <Button
                          variant="outline"
                          onClick={() => window.open(getWaveInvoiceUrl(invoice.wave_invoice_id!), '_blank')}
                          className="flex items-center"
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          View in Wave
                        </Button>
                      )}

                      {isAdmin && !invoice.scheduled_date && (
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              variant="default"
                              onClick={() => setSelectedInvoice(invoice)}
                            >
                              Schedule Invoice
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Schedule Invoice</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                              <div>
                                <Label htmlFor="scheduledDate">Scheduled Date</Label>
                                <Input
                                  id="scheduledDate"
                                  type="date"
                                  value={scheduledDate}
                                  onChange={(e) => setScheduledDate(e.target.value)}
                                />
                              </div>
                              <div className="flex justify-end space-x-2">
                                <Button variant="outline" onClick={() => setSelectedInvoice(null)}>
                                  Cancel
                                </Button>
                                <Button onClick={handleScheduleInvoice}>
                                  Schedule
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoicesPage;
